// src/components/contacts/OutboundContactWorkspacePanel.tsx
//
// Contact Workspace — CRM presentation layer (Outbound-parity).
//
// Reads the aggregated Outbound Contact Workspace DTO for a synced CRM
// contact and renders every Outbound-managed section, in the same
// order Outbound's own call workspace shows them:
//
//   1. Call Brief
//   2. Research Findings          (summary · pain points · talking points)
//   3. Website Intelligence       (website + business snapshot)
//   4. Review Details             (rating · review count · Google strength)
//   5. Social Signals             (research.socialSignals)
//   6. Prospect Background        (dossier: company summary, priorities…)
//   7. LinkedIn Research          (person + company snapshots)
//   8. Recent Activity            (activity feed merged with call history)
//
// STRICT rules enforced here:
//   • No writes. Read-only via RTK Query.
//   • No persistence. Nothing from the DTO is copied into a CRM store.
//   • Service token exists only on the CRM backend; the proxy attaches
//     it server-side — this component only sees the projected DTO.
//   • Each section renders independently. If Outbound reports one
//     section as `unavailable`, sibling sections continue rendering.
//   • Empty sections show discovery-question style copy (verbatim from
//     Outbound where possible) so the caller sees an actionable prompt
//     instead of a blank card.
//   • A CONTACT_NOT_SYNCED response shows a single explanatory line,
//     hiding all section shells.
//
// Feature flag lives in the caller (Phone page / slide-over).

import { useMemo, useState } from 'react';
import {
  useGetOutboundContactWorkspaceQuery,
  useTriggerOutboundResearchMutation,
  useTriggerOutboundContactLinkedInMutation,
} from '../../api/outboundContactWorkspaceApi';
import type {
  SectionStatus,
  OutboundActivityKind,
  OutboundContactWorkspaceOkResponse,
  OutboundResearchSectionData,
} from '../../api/outboundContactWorkspaceApi';
import WebsitePreviewInline from './WebsitePreviewInline';
import { useContactPermissions } from '../../hooks/useContactCompanyPermissions';
import { stripInternalAiTags } from '../../utils/aiText';

/**
 * Human label for a raw signal `kind` enum (e.g. `research_source` →
 * "Research Source") — mirrors how the rest of the workspace surfaces
 * enums as words, never raw snake_case.
 */
function humanizeKind(kind: string): string {
  return kind
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface Props {
  crmContactId: string;
}

// ── Copy tokens ────────────────────────────────────────────────────

// Verbatim from Outbound's CallQueueResearchSections.tsx so the caller
// sees the same discovery prompts on both surfaces.
const DISCOVERY_QUESTIONS = {
  callBrief: "What's the single outcome you want from this call?",
  research: "What's been your biggest challenge in growing the business this year?",
  website: 'How are most customers currently finding you today?',
  reviews: 'What kind of feedback do you hear most often from customers?',
  social:
    'Are social media channels currently part of your customer acquisition strategy?',
  dossier: 'How would you describe the business you run today?',
  linkedin: 'Who else is typically involved in decisions like this at your company?',
  activity: 'When did you last connect with this prospect?',
} as const;

const SOCIAL_EMPTY_COPY =
  'No social media presence was detected for this business.';

const UNAVAILABLE_COPY = 'This section is temporarily unavailable.';
const NO_RECENT_CALL_COPY = 'No recent completed call for this contact.';

function statusFallback(s: SectionStatus): string {
  switch (s) {
    case 'unavailable':
    case 'failed':
      return UNAVAILABLE_COPY;
    case 'no_recent_call':
      return NO_RECENT_CALL_COPY;
    default:
      return '';
  }
}

// ── Reusable primitives ────────────────────────────────────────────

function SectionShell({
  title,
  status,
  discoveryQuestion,
  isEmpty,
  children,
  footer,
  testTitle,
}: {
  title: string;
  status: SectionStatus;
  discoveryQuestion?: string;
  isEmpty?: boolean;
  children?: React.ReactNode;
  /** Optional slot rendered below the main body / fallback. Always
   *  visible regardless of empty/unavailable state — used to attach
   *  actionable affordances like the "Run Outbound research" button. */
  footer?: React.ReactNode;
  testTitle?: string;
}) {
  const slug = (testTitle ?? title).toLowerCase().replace(/\s+/g, '-');
  const showUnavailable = status !== 'ok' && status !== 'not_requested';
  const showBody = status === 'ok' && !isEmpty;
  return (
    <section
      data-testid={`outbound-workspace-section-${slug}`}
      data-status={status}
      className="rounded-xl border border-slate-200 bg-white shadow-sm p-4"
    >
      <p className="text-[10px] font-semibold uppercase tracking-widest text-indigo-500 mb-2">
        {title}
      </p>
      {showUnavailable ? (
        <p className="text-sm text-gray-500">{statusFallback(status)}</p>
      ) : showBody ? (
        children
      ) : discoveryQuestion ? (
        <DiscoveryQ question={discoveryQuestion} />
      ) : (
        <p className="text-sm text-gray-500">Nothing here yet.</p>
      )}
      {footer}
    </section>
  );
}

interface ResearchTriggerProps {
  crmContactId: string;
  /**
   * Current research section status from the DTO. The button renders
   * only for status values where triggering makes sense — never for
   * `ok` (already have research), and never for `unavailable` (the
   * upstream is degraded; ask the user to retry later, not fire more
   * research).
   */
  researchStatus: SectionStatus;
}

function ResearchTriggerButton({
  crmContactId,
  researchStatus,
}: ResearchTriggerProps) {
  const [trigger, state] = useTriggerOutboundResearchMutation();
  // Research SPENDS CREDITS; the server requires contacts.update. A principal
  // without it (viewer) never sees the button rather than meeting a 403.
  const { canRunOutboundContactActions } = useContactPermissions();

  // Button visible only for statuses where research CAN be triggered.
  // 'ok'          → nothing to do
  // 'unavailable' → upstream is down; suppress to avoid spamming
  const canTrigger =
    canRunOutboundContactActions &&
    (researchStatus === 'not_requested' || researchStatus === 'failed');

  // Last result governs the inline banner (success / failure / in_progress).
  const lastMessage = useMemo(() => {
    if (state.isSuccess && state.data) {
      const d = state.data;
      if (d.status === 'in_progress') {
        return { kind: 'info' as const, text: 'Research is running.' };
      }
      if (d.status === 'completed') {
        return { kind: 'success' as const, text: 'Research updated.' };
      }
      return {
        kind: 'error' as const,
        text: d.message || 'Research failed.',
      };
    }
    if (state.isError && state.error) {
      const e = state.error as {
        status?: number;
        data?: { error?: string; message?: string; retryAfterSeconds?: number };
      };
      if (e.status === 402) {
        return {
          kind: 'error' as const,
          text: e.data?.message || 'Not enough credits to run research.',
        };
      }
      if (e.status === 404 && e.data?.error === 'CONTACT_NOT_SYNCED') {
        return {
          kind: 'error' as const,
          text: 'Outbound cannot find this contact.',
        };
      }
      if (e.status === 429) {
        return {
          kind: 'info' as const,
          text:
            e.data?.message ||
            'Research is already running. Please wait a moment.',
        };
      }
      return {
        kind: 'error' as const,
        text: e.data?.message || 'Could not start research.',
      };
    }
    return null;
  }, [state]);

  if (!canTrigger && !lastMessage && !state.isLoading) return null;

  return (
    <div
      data-testid="cw-research-trigger"
      data-can-trigger={canTrigger ? 'true' : 'false'}
      className="mt-3 flex flex-wrap items-center gap-2"
    >
      {canTrigger ? (
        <button
          type="button"
          data-testid="cw-research-trigger-button"
          disabled={state.isLoading}
          onClick={() => {
            void trigger({ crmContactId }).unwrap().catch(() => {
              /* handled via state.error */
            });
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-wait"
        >
          {state.isLoading ? (
            <>
              <span
                aria-hidden
                className="inline-block w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin"
              />
              Running research…
            </>
          ) : (
            'Run Outbound research'
          )}
        </button>
      ) : null}
      {lastMessage ? (
        <span
          data-testid="cw-research-trigger-message"
          data-kind={lastMessage.kind}
          className={
            lastMessage.kind === 'error'
              ? 'text-xs text-red-700'
              : lastMessage.kind === 'success'
              ? 'text-xs text-emerald-700'
              : 'text-xs text-slate-600'
          }
        >
          {lastMessage.text}
        </span>
      ) : null}
    </div>
  );
}

function DiscoveryQ({ question }: { question: string }) {
  return (
    <div
      data-testid="discovery-question"
      className="rounded-lg bg-indigo-50/70 border border-indigo-100 px-3 py-2"
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-500 mb-0.5">
        Discovery question
      </p>
      <p className="text-xs text-indigo-800 leading-snug italic">
        &ldquo;{question}&rdquo;
      </p>
    </div>
  );
}

function Label({ text }: { text: string }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">
      {text}
    </p>
  );
}

/** Truncate long text with a "View more / Show less" toggle. Purely
 *  presentational — no state persistence, no fetching. Threshold in
 *  characters; if the text is shorter, renders as-is. */
function ExpandableText({
  text,
  threshold = 280,
  ariaLabel,
}: {
  text: string;
  threshold?: number;
  ariaLabel?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  if (text.length <= threshold) return <p>{text}</p>;
  const preview = expanded ? text : `${text.slice(0, threshold).trimEnd()}…`;
  return (
    <div data-testid="cw-expandable">
      <p>{preview}</p>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-label={ariaLabel ?? (expanded ? 'Show less' : 'View more')}
        data-testid="cw-expandable-toggle"
        className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
      >
        {expanded ? 'Show less' : 'View more'}
      </button>
    </div>
  );
}

/** Show at most `limit` items with a "View more / Show less" toggle. */
function ExpandableList<T>({
  items,
  limit = 5,
  render,
  testid,
}: {
  items: T[];
  limit?: number;
  render: (item: T, idx: number) => React.ReactNode;
  testid?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, limit);
  return (
    <div data-testid={testid}>
      <ul className="list-disc list-inside space-y-0.5">
        {visible.map((item, i) => (
          <li key={i}>{render(item, i)}</li>
        ))}
      </ul>
      {items.length > limit ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={testid ? `${testid}-toggle` : undefined}
          className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
        >
          {expanded ? 'Show less' : `View more (${items.length - limit})`}
        </button>
      ) : null}
    </div>
  );
}

// ── Section bodies ─────────────────────────────────────────────────

function ResearchBody({ dto }: { dto: OutboundContactWorkspaceOkResponse }) {
  const d = dto.research.data;
  if (!d) return null;
  return (
    <div className="space-y-3 text-sm text-slate-700">
      {d.summary ? (
        <ExpandableText
          text={stripInternalAiTags(d.summary)}
          ariaLabel="Research summary"
        />
      ) : null}
      {d.painPoints.length > 0 ? (
        <div>
          <Label text="Pain points" />
          <ExpandableList
            testid="cw-research-pain-points"
            items={d.painPoints}
            render={(p) => stripInternalAiTags(p)}
          />
        </div>
      ) : null}
      {d.talkingPoints.length > 0 ? (
        <div>
          <Label text="Talking points" />
          <ExpandableList
            testid="cw-research-talking-points"
            items={d.talkingPoints}
            render={(p) => stripInternalAiTags(p)}
          />
        </div>
      ) : null}
      {d.discoveryQuestions.length > 0 ? (
        <div>
          <Label text="Discovery questions" />
          <ExpandableList
            testid="cw-research-discovery-questions"
            items={d.discoveryQuestions}
            render={(q) => stripInternalAiTags(q)}
          />
        </div>
      ) : null}
    </div>
  );
}

function WebsiteBody({ dto }: { dto: OutboundContactWorkspaceOkResponse }) {
  const url = dto.websiteUrl ?? dto.research.data?.website ?? null;
  const research = dto.research.data;
  const hasAny =
    url ||
    research?.companyName ||
    research?.industry ||
    research?.companySize ||
    research?.location;
  if (!hasAny) return null;
  return (
    <div className="space-y-2 text-sm text-slate-700">
      {/* Live thumbnail via Outbound's WebsitePreviewService (Thum.io).
          Renders null when no URL; no iframe. */}
      <WebsitePreviewInline websiteUrl={url} />
      {url ? (
        <div>
          <Label text="Website" />
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline break-all"
          >
            {url}
          </a>
        </div>
      ) : null}
      {research?.companyName ? (
        <div>
          <Label text="Company" />
          <p>{research.companyName}</p>
        </div>
      ) : null}
      {research?.industry ? (
        <div>
          <Label text="Industry" />
          <p>{research.industry}</p>
        </div>
      ) : null}
      {research?.companySize ? (
        <div>
          <Label text="Company size" />
          <p>{research.companySize}</p>
        </div>
      ) : null}
      {research?.location ? (
        <div>
          <Label text="Location" />
          <p>{research.location}</p>
        </div>
      ) : null}
    </div>
  );
}

// ── Business snapshot card (Outbound parity) ─────────────────────

function BusinessSnapshotCard({
  dto,
}: {
  dto: OutboundContactWorkspaceOkResponse;
}) {
  const r = dto.research.data;
  if (!r) return null;
  const {
    companyName,
    industry,
    googleRating,
    googleReviewCount,
    website,
    googleMapsUrl,
    companyFoundedYear,
  } = r;
  const hasAny =
    companyName ||
    industry ||
    googleRating !== null ||
    website ||
    googleMapsUrl ||
    companyFoundedYear !== null;
  if (!hasAny) return null;

  const foundedLine = companyFoundedYear
    ? (() => {
        const nowYear = new Date().getFullYear();
        const age = nowYear - companyFoundedYear;
        return age >= 1
          ? `Founded ${companyFoundedYear} · ${age} year${age === 1 ? '' : 's'}`
          : `Founded ${companyFoundedYear}`;
      })()
    : null;

  return (
    <section
      data-testid="outbound-workspace-section-business"
      className="rounded-xl border border-slate-200 bg-white shadow-sm p-4"
    >
      <p className="text-[10px] font-semibold uppercase tracking-widest text-indigo-500 mb-2">
        Business
      </p>
      <div className="space-y-1.5 text-sm text-slate-700">
        {companyName ? (
          <p className="font-semibold text-slate-900">{companyName}</p>
        ) : null}
        {industry ? <p className="text-slate-600">{industry}</p> : null}
        {googleRating !== null || googleReviewCount !== null ? (
          <p
            data-testid="cw-business-rating"
            className="flex items-center gap-1 text-slate-700"
          >
            <span aria-hidden>⭐</span>
            <span className="font-medium">
              {googleRating !== null ? googleRating.toFixed(1) : '—'}
            </span>
            {googleReviewCount !== null ? (
              <span className="text-slate-500">
                ({googleReviewCount.toLocaleString()})
              </span>
            ) : null}
          </p>
        ) : null}
        {website ? (
          <a
            href={website}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cw-business-website-link"
            className="block text-blue-600 hover:underline break-all"
          >
            {website}
          </a>
        ) : null}
        {googleMapsUrl ? (
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cw-business-maps-link"
            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
          >
            <span aria-hidden>↗</span> Google Maps
          </a>
        ) : null}
        {foundedLine ? (
          <p data-testid="cw-business-founded" className="text-slate-500 text-xs">
            {foundedLine}
          </p>
        ) : null}
      </div>
    </section>
  );
}

// ── Pain points callout card (Outbound parity — yellow tinted) ───

function TopPainPointsCard({
  dto,
}: {
  dto: OutboundContactWorkspaceOkResponse;
}) {
  const painPoints = dto.research.data?.painPoints ?? [];
  if (painPoints.length === 0) return null;
  return (
    <section
      data-testid="outbound-workspace-section-pain-points"
      className="rounded-xl border border-amber-200 bg-amber-50/60 shadow-sm p-4"
    >
      <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 mb-2">
        Pain Points
      </p>
      <ul className="space-y-1 text-sm text-slate-800">
        {painPoints.slice(0, 6).map((p, i) => (
          <li key={i} className="flex gap-1.5">
            <span className="text-amber-500 select-none">•</span>
            <span>{stripInternalAiTags(p)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ReviewsBody({ dto }: { dto: OutboundContactWorkspaceOkResponse }) {
  const d = dto.research.data;
  if (!d) return null;
  const hasAny =
    d.googleRating !== null ||
    d.googleReviewCount !== null ||
    d.googleListingStrength ||
    d.recentReviewCount90d !== null ||
    d.reviewActivitySignal;
  if (!hasAny) return null;
  const has90dSignal =
    d.recentReviewCount90d !== null ||
    d.recentPositiveReviewCount90d !== null ||
    d.recentNegativeReviewCount90d !== null ||
    d.reviewActivitySignal;
  return (
    <div className="space-y-3 text-sm text-slate-700">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label text="Rating" />
          <p className="font-medium text-slate-900">
            {d.googleRating !== null ? d.googleRating.toFixed(1) : '—'}
          </p>
        </div>
        <div>
          <Label text="Reviews" />
          <p className="font-medium text-slate-900">
            {d.googleReviewCount !== null
              ? d.googleReviewCount.toLocaleString()
              : '—'}
          </p>
        </div>
        <div>
          <Label text="Listing" />
          <p className="font-medium text-slate-900 capitalize">
            {d.googleListingStrength ?? '—'}
          </p>
        </div>
      </div>
      {has90dSignal ? (
        <div data-testid="cw-reviews-activity-90d">
          <Label text="Activity (90 days)" />
          <div className="flex flex-wrap gap-1.5">
            {d.recentReviewCount90d !== null ? (
              <span
                data-testid="cw-reviews-90d-count"
                className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700"
              >
                {d.recentReviewCount90d} new
              </span>
            ) : null}
            {d.recentPositiveReviewCount90d !== null ? (
              <span
                data-testid="cw-reviews-90d-pos"
                className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-800"
              >
                {d.recentPositiveReviewCount90d} positive
              </span>
            ) : null}
            {d.recentNegativeReviewCount90d !== null ? (
              <span
                data-testid="cw-reviews-90d-neg"
                className="text-[10px] px-2 py-0.5 rounded bg-rose-100 text-rose-800"
              >
                {d.recentNegativeReviewCount90d} negative
              </span>
            ) : null}
            {d.reviewActivitySignal ? (
              <span
                data-testid="cw-reviews-90d-signal"
                className="text-[10px] px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 capitalize"
              >
                {d.reviewActivitySignal}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Genuine social-media signals only.
 *
 * The Outbound DTO stuffs research CITATIONS (kind `research_source`,
 * derived from pain/talking points that carry a source URL) into
 * `socialSignals`. Those are NOT social-media presence — Outbound's own
 * contact drawer ignores them and shows "No social media presence was
 * detected for this business." We match that exactly: `research_source`
 * entries never count as social signals.
 */
function realSocialSignals(dto: OutboundContactWorkspaceOkResponse) {
  return (dto.research.data?.socialSignals ?? []).filter(
    (s) => s.kind !== 'research_source',
  );
}

function SocialSignalsBody({ dto }: { dto: OutboundContactWorkspaceOkResponse }) {
  const items = realSocialSignals(dto);
  if (items.length === 0) return null;
  return (
    <ul className="space-y-1.5 text-sm text-slate-700">
      {items.map((s, i) => (
        <li key={i} className="flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-wide text-slate-400 flex-none">
            {humanizeKind(s.kind)}
          </span>
          <span className="min-w-0 flex-1">
            {s.url ? (
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline"
              >
                {stripInternalAiTags(s.excerpt)}
              </a>
            ) : (
              <span>{stripInternalAiTags(s.excerpt)}</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Prospect Background — parity with Outbound's `ProspectBackgroundSection`
 * (client/src/components/calls/CallQueueResearchSections.tsx). It renders
 * company name + category + age + description sourced from the RESEARCH
 * profile — NOT the `dossier` (which Outbound's contact drawer never
 * surfaces; the CRM used to wire this card to it, so it always fell back
 * to the discovery question).
 *
 * `businessDescription` / `companyAgeSummary` are optional DTO fields; when
 * the Outbound DTO carries them they render, and until then the card still
 * shows companyName / industry / a founded-year age line.
 */
function prospectAgeLine(r: OutboundResearchSectionData | null): string | null {
  if (r?.companyAgeSummary && r.companyAgeSummary.toLowerCase() !== 'unknown') {
    return r.companyAgeSummary;
  }
  if (typeof r?.companyFoundedYear === 'number') {
    return `Founded ${r.companyFoundedYear}`;
  }
  return null;
}

function ProspectBackgroundBody({
  dto,
}: {
  dto: OutboundContactWorkspaceOkResponse;
}) {
  const r = dto.research.data;
  const name = r?.companyName || null;
  const category = r?.industry || null;
  const age = prospectAgeLine(r);
  const desc = r?.businessDescription || null;
  if (!(name || category || age || desc)) return null;
  return (
    <div className="space-y-1" data-testid="cw-prospect-background">
      {name ? (
        <p className="text-sm font-semibold text-slate-800 leading-tight">
          {name}
        </p>
      ) : null}
      {category ? <p className="text-xs text-slate-500">{category}</p> : null}
      {age ? <p className="text-[10px] text-slate-400">{age}</p> : null}
      {desc ? (
        <p className="text-xs text-slate-600 leading-relaxed">
          {stripInternalAiTags(desc)}
        </p>
      ) : null}
    </div>
  );
}

function LinkedInBody({ dto }: { dto: OutboundContactWorkspaceOkResponse }) {
  const d = dto.linkedin.data;
  if (!d) return null;
  return (
    <div className="space-y-3 text-sm text-slate-700">
      {d.person ? (
        <div>
          <Label text="Person" />
          <p className="font-medium text-slate-900">{d.person.fullName ?? '—'}</p>
          {d.person.headline ? (
            <p className="text-slate-600">{d.person.headline}</p>
          ) : null}
          {d.person.currentRole || d.person.currentCompany ? (
            <p className="text-slate-500 text-xs">
              {[d.person.currentRole, d.person.currentCompany]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : null}
          {d.person.linkedinUrl ? (
            <a
              href={d.person.linkedinUrl}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline text-xs"
            >
              View profile
            </a>
          ) : null}
        </div>
      ) : null}
      {d.company ? (
        <div>
          <Label text="Company" />
          <p className="font-medium text-slate-900">{d.company.name ?? '—'}</p>
          {d.company.industry ? (
            <p className="text-slate-600">{d.company.industry}</p>
          ) : null}
          {d.company.companySize ? (
            <p className="text-slate-500 text-xs">{d.company.companySize}</p>
          ) : null}
          {d.company.linkedinUrl ? (
            <a
              href={d.company.linkedinUrl}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline text-xs"
            >
              View company
            </a>
          ) : null}
        </div>
      ) : null}
      {d.confidence ? (
        <p className="text-[10px] uppercase tracking-wide text-slate-400">
          Confidence: {d.confidence}
        </p>
      ) : null}
    </div>
  );
}

const ACTIVITY_BADGE: Record<
  OutboundActivityKind,
  { label: string; className: string; testid: string }
> = {
  call: {
    label: 'Call',
    className: 'bg-emerald-100 text-emerald-800',
    testid: 'cw-activity-badge-call',
  },
  email: {
    label: 'Email',
    className: 'bg-blue-100 text-blue-800',
    testid: 'cw-activity-badge-email',
  },
  sms: {
    label: 'SMS',
    className: 'bg-amber-100 text-amber-800',
    testid: 'cw-activity-badge-sms',
  },
  callback_scheduled: {
    label: 'Callback',
    className: 'bg-violet-100 text-violet-800',
    testid: 'cw-activity-badge-callback',
  },
  callback_rescheduled: {
    label: 'Callback',
    className: 'bg-violet-100 text-violet-800',
    testid: 'cw-activity-badge-callback',
  },
  other: {
    label: 'Other',
    className: 'bg-slate-100 text-slate-700',
    testid: 'cw-activity-badge-other',
  },
};

/** Display-only timestamp: relative for recent items, short absolute
 *  for anything older. Purely presentational; no persistence. */
function formatActivityTimestamp(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const diffMs = Date.now() - t;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 7) return `${days}d ago`;
  // Older: short absolute
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year:
      d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

function ActivityBody({ dto }: { dto: OutboundContactWorkspaceOkResponse }) {
  // Merge activity + call history so the surface matches Outbound's
  // single "Recent Activity" section. Dedupe by id, then sort newest
  // first — belt-and-suspenders since the Outbound feed is already
  // descending but we don't want to trust cross-source ordering.
  const seen = new Set<string>();
  const items = [
    ...dto.activity.items,
    ...dto.callHistory.items,
  ].filter((it) => {
    if (seen.has(it.id)) return false;
    seen.add(it.id);
    return true;
  });
  items.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  if (items.length === 0) return null;

  return (
    <ul
      data-testid="cw-activity-list"
      className="divide-y divide-slate-100 text-sm text-slate-700"
    >
      {items.slice(0, 12).map((it) => {
        const badge = ACTIVITY_BADGE[it.kind] ?? ACTIVITY_BADGE.other;
        return (
          <li
            key={it.id}
            data-testid="cw-activity-item"
            data-kind={it.kind}
            className="py-2 flex items-baseline gap-2"
          >
            <span
              data-testid={badge.testid}
              className={`text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 flex-none ${badge.className}`}
            >
              {badge.label}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-900 truncate">{it.title}</p>
              {it.subtitle ? (
                <p className="text-xs text-slate-500 truncate">{it.subtitle}</p>
              ) : null}
            </div>
            {it.details.callOutcome ? (
              <span className="text-[10px] text-slate-500 flex-none capitalize">
                {it.details.callOutcome}
              </span>
            ) : null}
            <time
              dateTime={it.at}
              className="text-[10px] text-slate-400 flex-none tabular-nums"
            >
              {formatActivityTimestamp(it.at)}
            </time>
          </li>
        );
      })}
    </ul>
  );
}

// ── Empty-state predicates ─────────────────────────────────────────

function isResearchEmpty(dto: OutboundContactWorkspaceOkResponse): boolean {
  const d = dto.research.data;
  if (!d) return true;
  return (
    !d.summary &&
    d.painPoints.length === 0 &&
    d.talkingPoints.length === 0 &&
    d.discoveryQuestions.length === 0
  );
}
function isWebsiteEmpty(dto: OutboundContactWorkspaceOkResponse): boolean {
  const url = dto.websiteUrl;
  const r = dto.research.data;
  return (
    !url &&
    !r?.website &&
    !r?.companyName &&
    !r?.industry &&
    !r?.companySize &&
    !r?.location
  );
}
function isReviewsEmpty(dto: OutboundContactWorkspaceOkResponse): boolean {
  const d = dto.research.data;
  if (!d) return true;
  return (
    d.googleRating === null &&
    d.googleReviewCount === null &&
    !d.googleListingStrength
  );
}
function isSocialEmpty(dto: OutboundContactWorkspaceOkResponse): boolean {
  // Only genuine social signals count — `research_source` citations don't
  // (Outbound's drawer shows "No social media presence…" for those).
  return realSocialSignals(dto).length === 0;
}
function isProspectBackgroundEmpty(
  dto: OutboundContactWorkspaceOkResponse,
): boolean {
  const r = dto.research.data;
  return !(
    r?.companyName ||
    r?.industry ||
    prospectAgeLine(r) ||
    r?.businessDescription
  );
}
function isLinkedInEmpty(dto: OutboundContactWorkspaceOkResponse): boolean {
  const d = dto.linkedin.data;
  if (!d) return true;
  return !d.person && !d.company;
}
function isActivityEmpty(dto: OutboundContactWorkspaceOkResponse): boolean {
  return dto.activity.items.length === 0 && dto.callHistory.items.length === 0;
}

// ── Run LinkedIn Research button ────────────────────────────────

interface LinkedInTriggerProps {
  crmContactId: string;
  linkedinStatus: SectionStatus;
  hasSnapshotData: boolean;
}

function LinkedInTriggerButton({
  crmContactId,
  linkedinStatus,
  hasSnapshotData,
}: LinkedInTriggerProps) {
  const [trigger, state] = useTriggerOutboundContactLinkedInMutation();
  // Same gate as research: spends credits, server requires contacts.update.
  const { canRunOutboundContactActions } = useContactPermissions();

  // Show the trigger only when there's no useful snapshot to display
  // (matches Outbound's own visibility). Once a snapshot exists, the
  // caller can re-run research via the Refresh action in Call Brief.
  const canTrigger =
    canRunOutboundContactActions &&
    !hasSnapshotData &&
    (linkedinStatus === 'not_requested' ||
      linkedinStatus === 'failed' ||
      linkedinStatus === 'ok');

  const lastMessage = useMemo(() => {
    if (state.isSuccess && state.data) {
      const d = state.data;
      if (d.status === 'in_progress') {
        return { kind: 'info' as const, text: 'Research is running.' };
      }
      if (d.status === 'completed') {
        return { kind: 'success' as const, text: 'LinkedIn research updated.' };
      }
      return { kind: 'error' as const, text: d.message || 'LinkedIn research failed.' };
    }
    if (state.isError && state.error) {
      const e = state.error as {
        status?: number;
        data?: { error?: string; message?: string };
      };
      if (e.status === 402) {
        return {
          kind: 'error' as const,
          text: e.data?.message || 'Not enough credits to run LinkedIn research.',
        };
      }
      if (e.status === 404 && e.data?.error === 'CONTACT_NOT_SYNCED') {
        return {
          kind: 'error' as const,
          text: 'Outbound cannot find this contact.',
        };
      }
      return {
        kind: 'error' as const,
        text: e.data?.message || 'Could not start LinkedIn research.',
      };
    }
    return null;
  }, [state]);

  if (!canTrigger && !lastMessage && !state.isLoading) return null;

  return (
    <div
      data-testid="cw-linkedin-trigger"
      data-can-trigger={canTrigger ? 'true' : 'false'}
      className="mt-3 flex flex-wrap items-center gap-2"
    >
      {canTrigger ? (
        <button
          type="button"
          data-testid="cw-linkedin-trigger-button"
          disabled={state.isLoading}
          onClick={() => {
            void trigger({ crmContactId }).unwrap().catch(() => {
              /* handled via state.error */
            });
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-wait"
        >
          {state.isLoading ? (
            <>
              <span
                aria-hidden
                className="inline-block w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin"
              />
              Running…
            </>
          ) : (
            'Run LinkedIn Research'
          )}
        </button>
      ) : null}
      {lastMessage ? (
        <span
          data-testid="cw-linkedin-trigger-message"
          data-kind={lastMessage.kind}
          className={
            lastMessage.kind === 'error'
              ? 'text-xs text-red-700'
              : lastMessage.kind === 'success'
              ? 'text-xs text-emerald-700'
              : 'text-xs text-slate-600'
          }
        >
          {lastMessage.text}
        </span>
      ) : null}
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────────────

export default function OutboundContactWorkspacePanel({ crmContactId }: Props) {
  const { data, error, isLoading, isFetching } =
    useGetOutboundContactWorkspaceQuery(crmContactId, { skip: !crmContactId });

  const errKind = useMemo<'not_synced' | 'unavailable' | null>(() => {
    if (!error) return null;
    const e = error as { status?: number; data?: { error?: string } };
    if (e.status === 404 && e.data?.error === 'CONTACT_NOT_SYNCED') return 'not_synced';
    return 'unavailable';
  }, [error]);

  if (!crmContactId) return null;

  if (isLoading) {
    return (
      <div
        data-testid="outbound-workspace-loading"
        className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500"
      >
        Loading Outbound workspace…
      </div>
    );
  }

  if (errKind === 'not_synced') {
    return (
      <div
        data-testid="outbound-workspace-not-synced"
        className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600"
      >
        Outbound workspace not available for this contact.
      </div>
    );
  }

  if (errKind === 'unavailable' || !data) {
    return (
      <div
        data-testid="outbound-workspace-unavailable"
        className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600"
      >
        Outbound is temporarily unavailable.
      </div>
    );
  }

  return (
    <div
      data-testid="outbound-workspace-panel"
      data-refreshing={isFetching ? 'true' : 'false'}
      className="space-y-3"
    >
      {/* Section order mirrors Outbound's contact drawer:
          Business → Pain Points → Research Findings → Website Intelligence →
          Review Details → Social Signals → Prospect Background →
          LinkedIn Research → Recent Activity.
          NOTE: Outbound's contact drawer has NO "Call Brief" card, so the
          CRM parity view intentionally omits it too — research surfaces
          below carry the same content without the redundant brief card. */}

      <BusinessSnapshotCard dto={data} />
      <TopPainPointsCard dto={data} />

      <SectionShell
        title="Research Findings"
        testTitle="research"
        status={data.research.status}
        discoveryQuestion={DISCOVERY_QUESTIONS.research}
        isEmpty={isResearchEmpty(data)}
        footer={
          <ResearchTriggerButton
            crmContactId={crmContactId}
            researchStatus={data.research.status}
          />
        }
      >
        <ResearchBody dto={data} />
      </SectionShell>

      <SectionShell
        title="Website Intelligence"
        testTitle="website"
        status={data.research.status}
        discoveryQuestion={DISCOVERY_QUESTIONS.website}
        isEmpty={isWebsiteEmpty(data)}
      >
        <WebsiteBody dto={data} />
      </SectionShell>

      <SectionShell
        title="Review Details"
        testTitle="reviews"
        status={data.research.status}
        discoveryQuestion={DISCOVERY_QUESTIONS.reviews}
        isEmpty={isReviewsEmpty(data)}
      >
        <ReviewsBody dto={data} />
      </SectionShell>

      {/* Social Signals — when genuine social signals exist, render them;
          when empty, show the exact Outbound copy line ABOVE the discovery
          question (matches Outbound's contact drawer). The body owns BOTH
          states, so `isEmpty` is intentionally NOT passed to SectionShell
          (which would otherwise swap the body for the fallback and hide the
          empty copy). */}
      <SectionShell
        title="Social Signals"
        testTitle="social"
        status={data.research.status}
      >
        {isSocialEmpty(data) ? (
          <>
            <p
              data-testid="cw-social-empty"
              className="text-xs text-slate-500 mb-2"
            >
              {SOCIAL_EMPTY_COPY}
            </p>
            <DiscoveryQ question={DISCOVERY_QUESTIONS.social} />
          </>
        ) : (
          <SocialSignalsBody dto={data} />
        )}
      </SectionShell>

      <SectionShell
        title="Prospect Background"
        testTitle="dossier"
        status={data.research.status}
        discoveryQuestion={DISCOVERY_QUESTIONS.dossier}
        isEmpty={isProspectBackgroundEmpty(data)}
      >
        <ProspectBackgroundBody dto={data} />
      </SectionShell>

      <SectionShell
        title="LinkedIn Research"
        testTitle="linkedin"
        status={data.linkedin.status}
        discoveryQuestion={DISCOVERY_QUESTIONS.linkedin}
        isEmpty={isLinkedInEmpty(data)}
        footer={
          <LinkedInTriggerButton
            crmContactId={crmContactId}
            linkedinStatus={data.linkedin.status}
            hasSnapshotData={!isLinkedInEmpty(data)}
          />
        }
      >
        <LinkedInBody dto={data} />
      </SectionShell>

      <SectionShell
        title="Recent Activity"
        testTitle="activity"
        status={data.activity.status}
        discoveryQuestion={DISCOVERY_QUESTIONS.activity}
        isEmpty={isActivityEmpty(data)}
      >
        <ActivityBody dto={data} />
      </SectionShell>
    </div>
  );
}
